import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Button, InpockInput, Paragraph1, Paragraph2 } from 'inpock-ui-react';

import { Accordion, Timer, TimerForwardedRef } from '@/components';
import { CERT_NUM_RULE, CUSTOMER_NAME, CUSTOMER_PHONE, EMAIL } from '@/constants';
import { useCertNumber, useForm, useToast } from '@/hooks';
import { useAuth } from '@/hooks/useAuth';
import { NonMemberAuthStorage } from '@/utils';
import { useSectionContext } from '@/components/checkout/SectionContext';

import { BuyerInfo, FormProps } from '../types';

interface BuyerSectionProps extends FormProps<BuyerInfo> {}

const TIMEOUT_MIN = 3;
const TIMEOUT_SEC = 0;

export function BuyerSection({ form, setForm, isOpen, customer }: BuyerSectionProps) {
  const { buyerSectionRef } = useSectionContext();
  const { loggedIn } = useAuth();
  const toastOn = useToast();
  const timerRef = useRef<TimerForwardedRef>(null);
  const [isCerting, setIsCerting] = useState<boolean>(false);

  const {
    createCertNumber,
    creatingCertNumber,
    created,
    verifyCertNumber,
    verifyingCertNumber,
    verified,
    resetCertNumber,
  } = useCertNumber();

  const { form: buyerForm, onInput, inputRef, register } = useForm(form);

  useImperativeHandle(buyerSectionRef, () => ({
    ...inputRef.current,
  }));

  useEffect(() => {
    if (loggedIn) {
      const newBuyForm = {
        ...form,
        buyer_name: customer?.name || '',
        buyer_phone: customer?.phone || '',
        buyer_email: customer?.email || '',
        isCertificated: true,
      };

      Object.entries(newBuyForm).forEach(([k, v]) => onInput(v as string, k));
      setForm(newBuyForm);
    }
  }, [loggedIn, customer]);

  const headerContent = () =>
    buyerForm.buyer_name || buyerForm.buyer_phone
      ? `${buyerForm.buyer_name}, ${buyerForm.buyer_phone}`
      : '-';

  const handleGetCert = async () => {
    timerRef.current?.reset();
    try {
      await createCertNumber(buyerForm.buyer_phone);
      toastOn('SMS를 통해 인증번호가 전송되었습니다');
      setIsCerting(true);
    } catch (error) {
      if (error instanceof Error) toastOn(error.message);
      else throw error;
    }
  };

  const handleVerify = async () => {
    if (!created) return toastOn('인증번호를 발급 받으세요');

    try {
      const { token } = await verifyCertNumber(
        buyerForm.buyer_phone,
        buyerForm.cert_number,
        undefined,
        true,
      );
      setForm({ ...form, isCertificated: true });

      const storage = new NonMemberAuthStorage();
      storage.set({
        phone: buyerForm.buyer_phone,
        cert_number: buyerForm.cert_number,
        token,
        expired_at: new Date(new Date().getTime() + 30 * 60 * 1000),
      });

      toastOn('인증에 성공했습니다');
    } catch (error) {
      if (error instanceof Error) toastOn(error.message);
      else throw error;
    }
  };

  const handleResetCert = () => {
    register('cert_number').onInput('', 'cert_number');
    register('cert_number').onInvalid('', 'cert_number');
    new NonMemberAuthStorage().clear();
    resetCertNumber();
    setForm({ ...form, isCertificated: false });
  };

  const onTimeOut = () => {
    timerRef.current?.clear();
    setIsCerting(false);
  };

  useEffect(() => {
    Object.entries(form).forEach(([k, v]) => onInput(v, k));
  }, [onInput, form]);

  return (
    <Accordion
      open={isOpen}
      firstRow={<Paragraph1 fontWeight="bold">구매자 정보</Paragraph1>}
      secondRow={<Paragraph2 color="gray500">{headerContent()}</Paragraph2>}
    >
      <section className="mt-24 mb-30">
        <InpockInput
          ref={(el) => (inputRef.current['buyer_name'] = el)}
          value={buyerForm.buyer_name}
          {...register('buyer_name')}
          onInput={(input_value) => {
            onInput(input_value, 'buyer_name');
            setForm((prev) => ({ ...prev, buyer_name: input_value }));
          }}
          type="text"
          guide="구매자명"
          required
          width="100%"
          disabled={!!customer?.name}
          maxLength={CUSTOMER_NAME.rules?.max_length}
          validator={(_rules, value) =>
            CUSTOMER_NAME.validator && CUSTOMER_NAME.validator(CUSTOMER_NAME.rules, value)
          }
          rules={CUSTOMER_NAME.rules}
        />

        <InpockInput
          ref={(el) => (inputRef.current['buyer_email'] = el)}
          value={buyerForm.buyer_email}
          {...register('buyer_email')}
          onInput={(input_value) => {
            onInput(input_value, 'buyer_email');
            setForm((prev) => ({ ...prev, buyer_email: input_value }));
          }}
          type="text"
          guide="이메일"
          required
          width="100%"
          disabled={!!customer?.email}
          validator={EMAIL.validator}
          trigger={['change']}
        />

        <InpockInput
          ref={(el) => (inputRef.current['buyer_phone'] = el)}
          value={buyerForm.buyer_phone}
          {...register('buyer_phone')}
          onInput={(input_value) => {
            onInput(input_value, 'buyer_phone');
            setForm((prev) => ({ ...prev, buyer_phone: input_value }));
          }}
          type="text"
          inputMode="numeric"
          placeholder="01000000000"
          guide={{
            message: '구매자 연락처',
            popupMessage: '주문 정보를 포함한 알림톡이 전송 돼요!',
          }}
          required
          width="100%"
          maxLength={CUSTOMER_PHONE.rules?.max_length}
          disabled={verified || created || !!customer?.phone}
          validator={(_rules, value) =>
            CUSTOMER_PHONE.validator && CUSTOMER_PHONE.validator(CUSTOMER_PHONE.rules, value)
          }
          rules={CUSTOMER_PHONE.rules}
          postfix={
            verified || created ? (
              <Button size="medium" type="line" onClick={handleResetCert}>
                변경
              </Button>
            ) : (
              <Button
                size="medium"
                type="secondary"
                onClick={handleGetCert}
                loading={creatingCertNumber}
                disabled={
                  created ||
                  buyerForm.buyer_phone.length < (CUSTOMER_PHONE.rules.min_length as number) ||
                  !!customer?.phone
                }
              >
                인증번호 받기
              </Button>
            )
          }
        />

        {created && !verified && (
          <InpockInput
            ref={(el) => (inputRef.current['cert_number'] = el)}
            value={buyerForm.cert_number}
            {...register('cert_number')}
            type="text"
            guide="인증번호"
            required
            disabled={verified}
            width="100%"
            maxLength={CERT_NUM_RULE.max_length}
            onKeyPress={(value) => value.key === 'Enter' && handleVerify()}
            postfix={
              <div className="d-flex">
                <div className="mr-10">
                  <Button
                    size="medium"
                    type="secondary"
                    onClick={handleVerify}
                    disabled={
                      verified || buyerForm.cert_number?.length !== CERT_NUM_RULE.min_length
                    }
                    loading={verifyingCertNumber}
                  >
                    확인
                  </Button>
                </div>
                {isCerting && !verified && (
                  <Timer ref={timerRef} sec={TIMEOUT_SEC} min={TIMEOUT_MIN} onTimeOut={onTimeOut} />
                )}
              </div>
            }
          />
        )}
      </section>
    </Accordion>
  );
}
